import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { VisitorSearchParams, VisitorTab } from "../../lib/visitor-search";

interface UseVisitorURLStateOptions {
  initialViewParam?: "list" | undefined;
  categoryParam?: string;
  searchInputRef: React.RefObject<HTMLInputElement>;
  onSearchClear: () => void;
}

interface UseVisitorURLStateResult {
  isListViewOpen: boolean;
  isSearchMode: boolean;
  openListView: () => void;
  closeListView: () => void;
  setTab: (tab: VisitorTab) => void;
}

/**
 * Manages URL-synced view state for visitor map (list view + tab switching).
 * List view uses window.history.pushState for perf; tab switching uses TanStack navigate.
 */
export function useVisitorURLState({
  initialViewParam,
  categoryParam,
  searchInputRef,
  onSearchClear,
}: UseVisitorURLStateOptions): UseVisitorURLStateResult {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as VisitorSearchParams;

  const isListViewFromUrl = initialViewParam === "list";
  const [isListViewOpen, setIsListViewOpen] = useState(isListViewFromUrl);
  const [isSearchMode, setIsSearchMode] = useState(isListViewFromUrl);

  // Immediately sync if URL state differs from local state (handles navigation)
  if (isListViewFromUrl !== isListViewOpen) {
    setIsListViewOpen(isListViewFromUrl);
    setIsSearchMode(isListViewFromUrl);
  }

  // URL builder helper — preserves tab param from current URL
  const buildUrl = useCallback(
    (newView: "list" | undefined) => {
      const currentParams = new URLSearchParams(window.location.search);
      const params = new URLSearchParams();
      const currentTab = currentParams.get("tab");
      if (currentTab && currentTab !== "map") params.set("tab", currentTab);
      if (categoryParam) params.set("category", categoryParam);
      if (newView) params.set("view", newView);
      const queryString = params.toString();
      return queryString ? `?${queryString}` : window.location.pathname;
    },
    [categoryParam],
  );

  const openListView = useCallback(() => {
    const url = buildUrl("list");
    window.history.pushState({}, "", url);
    setIsListViewOpen(true);
    setIsSearchMode(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [buildUrl, searchInputRef]);

  const closeListView = useCallback(() => {
    // Only rewrite URL when legacy list view is actually open. Otherwise this
    // racing pushState clobbers concurrent TanStack navigations (e.g. View on
    // Map switching tab=map while on view=sponsors sub-page).
    if (isListViewOpen) {
      const url = buildUrl(undefined);
      window.history.pushState({}, "", url);
    }
    setIsListViewOpen(false);
    setIsSearchMode(false);
    onSearchClear();
  }, [buildUrl, isListViewOpen, onSearchClear]);

  // Tab switching — strips poi param (intentional context change)
  const setTab = useCallback(
    (tab: VisitorTab) => {
      void navigate({ to: ".", search: { ...search, tab, poi: undefined } });
    },
    [navigate, search],
  );

  // Sync list view with URL param changes
  useEffect(() => {
    const isListView = initialViewParam === "list";
    setIsListViewOpen(isListView);
    setIsSearchMode(isListView);
    if (isListView) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [initialViewParam, searchInputRef]);

  // Listen to popstate (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const isListView = params.get("view") === "list";
      setIsListViewOpen(isListView);
      setIsSearchMode(isListView);
      if (!isListView) onSearchClear();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [onSearchClear]);

  // ESC key closes list view
  useEffect(() => {
    if (!isListViewOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeListView();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isListViewOpen, closeListView]);

  return {
    isListViewOpen,
    isSearchMode,
    openListView,
    closeListView,
    setTab,
  };
}
